import { useTranslation } from "react-i18next";
import { Image, Camera, Upload, AlertCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { PageLoadingSpinner } from "@/shared/components/ui/page-loading-spinner";
import { toast } from "sonner";
import { addAuthTokenToUrl } from "@/shared/utils/sseConnection";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

interface OrganizationLogoUploadProps {
  organizationId?: string;
  currentLogoUrl: string | null;
  onLogoUpdate: (url: string, file?: File) => void;
}

export const OrganizationLogoUpload = ({
  currentLogoUrl,
  onLogoUpdate,
}: OrganizationLogoUploadProps) => {
  const { t } = useTranslation();
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentLogoUrl);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isUploading] = useState(false);

  // Atualizar o previewUrl sempre que currentLogoUrl mudar
  useEffect(() => {
    if (currentLogoUrl) {
      setPreviewUrl(currentLogoUrl);
      setImageError(false);
    }
  }, [currentLogoUrl]);

  // Função para adicionar token e timestamp à URL para evitar cache e garantir acesso autenticado
  const getAuthorizedUrl = (url: string): string => {
    if (!url) {
      return '';
    }

    const urlWithToken = addAuthTokenToUrl(url);
    const separator = urlWithToken.includes('?') ? '&' : '?';
    return `${urlWithToken}${separator}_ts=${Date.now()}`;
  };

  // Verificar se a URL é uma object URL (blob:)
  const isObjectUrl = (url: string): boolean => {
    return url?.startsWith('blob:') || url?.startsWith('data:');
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      if (!file.type.startsWith('image/')) {
        toast.error(t('settings.organization.logoUpload.invalidType'));
        event.target.value = '';
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error(t('settings.organization.logoUpload.invalidSize'));
        event.target.value = '';
        return;
      }

      // Limpar o input para permitir selecionar o mesmo arquivo novamente
      event.target.value = '';
      
      setIsPreviewLoading(true);
      setImageError(false);
      
      // Usar FileReader para criar uma URL de dados (data URL) para preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        
        // Atualizar o estado para exibir a imagem
        setPreviewUrl(dataUrl);
        setIsPreviewLoading(false);
        
        // Notificar o componente pai sobre o arquivo selecionado e a URL de dados
        onLogoUpdate(dataUrl, file);
      };
      
      // Iniciar a leitura do arquivo como uma URL de dados
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('[OrganizationLogoUpload] Erro ao processar o arquivo:', error);
      toast.error(t('settings.organization.logoUpload.uploadError') || 'Error uploading image');
      setImageError(true);
      setIsPreviewLoading(false);
    }
  };

  return (
    <div className="space-y-2 w-1/4">
      <label className="text-sm font-medium">
        {t("settings.organization.logo")}
      </label>
      <div className="relative group w-full h-12 rounded-lg overflow-hidden">
        <label 
          htmlFor="organization-logo-upload" 
          className="block w-full h-full cursor-pointer bg-muted"
        >
          {previewUrl && !imageError ? (
            <img
              key={`img-${previewUrl}`}
              src={isObjectUrl(previewUrl) ? previewUrl : getAuthorizedUrl(previewUrl)}
              alt={t("settings.organization.logo")}
              className="w-full h-full object-contain"
              onLoad={() => setImageError(false)}
              onError={() => setImageError(true)}
            />
          ) : imageError ? (
            <div className="flex flex-col items-center justify-center w-full h-full text-muted-foreground">
              <AlertCircle className="w-4 h-4" />
              <span className="text-xs">{t('common.error')}</span>
            </div>
          ) : (
            <div className="flex items-center justify-center w-full h-full">
              <Image className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
            <Camera className="w-4 h-4 text-white" />
          </div>
          {isPreviewLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <Upload className="w-4 h-4 text-white animate-bounce" />
            </div>
          )}
          {isUploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <PageLoadingSpinner size="sm" showText={false} className="text-white" />
            </div>
          )}
        </label>
        <input
          id="organization-logo-upload"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleLogoUpload}
          disabled={isUploading || isPreviewLoading}
        />
      </div>
    </div>
  );
};
